import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Loader from 'ui-component/Loader';
import MainLayout from 'layout/MainLayout';
import CompleteAddressPage from 'views/pages/authentication/CompleteAddressPage';
import useAuth from 'hooks/useAuth';
import {
  clearAddressProfileRequired,
  fetchCustomerProfile,
  getLoginCustomerId,
  isAddressProfileComplete,
  isAddressProfileRequired,
  isAddressProfileShellReady,
  markAddressProfileShellReady,
  markAddressProfileSkipToast,
  setAddressProfileRequired
} from 'utils/address/addressProfile';

/**
 * Mirrors legacy App.tsx shell gating: verify business ADDRESS before rendering MainLayout.
 * Users without address are redirected to /complete-address (no sidebar/header).
 */
export default function AddressVerifiedLayout() {
  const { isLoggedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [canShowAppShell, setCanShowAppShell] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      setCanShowAppShell(false);
      return;
    }

    if (location.pathname === '/complete-address') {
      setCanShowAppShell(false);
      return;
    }

    if (isAddressProfileShellReady()) {
      setCanShowAppShell(true);
      return;
    }

    if (isAddressProfileRequired()) {
      if (location.pathname !== '/complete-address') {
        markAddressProfileSkipToast();
      }
      navigate('/complete-address', {
        replace: true,
        state: { from: location }
      });
      setCanShowAppShell(false);
      return;
    }

    let cancelled = false;
    setCanShowAppShell(false);

    const verifyAddressBeforeShell = async () => {
      const customerId = getLoginCustomerId();
      if (!customerId) {
        if (!cancelled) setCanShowAppShell(true);
        return;
      }

      try {
        const customer = await fetchCustomerProfile(customerId);
        if (cancelled) return;

        if (!customer) {
          if (!cancelled) setCanShowAppShell(true);
          return;
        }

        if (!isAddressProfileComplete(customer)) {
          setAddressProfileRequired();
          navigate('/complete-address', {
            replace: true,
            state: { from: location }
          });
          return;
        }

        markAddressProfileShellReady();
        clearAddressProfileRequired();
        setCanShowAppShell(true);
      } catch {
        if (!cancelled) setCanShowAppShell(true);
      }
    };

    void verifyAddressBeforeShell();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, location.pathname, navigate]);

  if (!isLoggedIn) {
    return null;
  }

  if (location.pathname === '/complete-address') {
    return <CompleteAddressPage />;
  }

  if (!canShowAppShell) {
    return <Loader />;
  }

  return <MainLayout />;
}
